import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import {
  profile,
  stats,
  brands,
  awards,
  furtherHonours,
  news,
} from "../data/profileData";
import MantraTicker from "../components/MantraTicker";
import { Reveal, staggerContainer, staggerItem } from "../components/Reveal";
import Seo, { buildProfileSchema } from "../components/Seo";

export default function Home() {
  const featuredAwards = [...awards, ...furtherHonours]
    .filter((a) => a.image)
    .slice(0, 5);
  const featuredNews = news.filter((n) => n.image).slice(0, 2);

  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        path="/"
        type="profile"
        description="Dr. P. B. Boss — entrepreneur, philanthropist and Founder & Chairman of the Osaka Group. Honorary Tourism Commissioner — India to the European Union, with 33+ years of leadership in global travel & tourism."
        schema={buildProfileSchema()}
      />
      {/* =====================================================
          HERO — couture editorial split
          ===================================================== */}
      <section
        data-testid="hero-section"
        className="relative min-h-[100svh] overflow-hidden"
      >
        {/* Vertical edge labels */}
        <span className="hidden lg:block absolute left-7 top-1/2 -translate-y-1/2 v-label">
          P · B · Boss — Est. 1993
        </span>
        <span className="hidden lg:block absolute right-7 top-1/2 -translate-y-1/2 v-label">
          Vol. I — The Profile
        </span>

        <div className="relative max-w-[1640px] mx-auto px-8 sm:px-20 lg:px-28 pt-36 lg:pt-44 pb-16 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center min-h-[100svh]">
          {/* LEFT — credentials + name */}
          <div className="lg:col-span-7">
            {/* Dual credentials */}
            <div
              data-testid="hero-overline"
              className="reveal-fade flex flex-col gap-3 mb-12"
            >
              <div className="flex items-center gap-4">
                <span className="font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--ink)]">
                  Founder & Chairman
                </span>
                <span className="rule-thin" style={{ width: 28 }} />
                <span className="font-couture-italic text-base text-[var(--gold-deep)]">
                  Osaka Group
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)]">
                  Hon. Tourism Commissioner
                </span>
                <span className="rule-thin" style={{ width: 28 }} />
                <span className="font-couture-italic text-base text-[var(--ink)]">
                  India to the European Union
                </span>
              </div>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-couture text-[clamp(4.5rem,12vw,11rem)] leading-[0.86] tracking-[-0.025em] text-[var(--ink)]"
            >
              <span className="rise-word">
                <span style={{ animationDelay: "120ms" }}>Dr.</span>
              </span>{" "}
              <span className="rise-word">
                <span style={{ animationDelay: "240ms" }}>P. B.</span>
              </span>
              <br />
              <span className="rise-word">
                <span
                  className="italic font-couture-italic text-[var(--gold-deep)]"
                  style={{ animationDelay: "380ms" }}
                >
                  Boss.
                </span>
              </span>
            </h1>

            {/* Mantras */}
            <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 reveal reveal-d3">
              {profile.mantras.map((m, i) => (
                <div key={m} className="flex items-center gap-6">
                  <span className="font-couture-italic text-2xl lg:text-[1.7rem] text-[var(--ink)]">
                    {m}
                  </span>
                  {i < profile.mantras.length - 1 && (
                    <span className="text-[var(--gold)] text-xs">✦</span>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-16 flex flex-wrap items-center gap-12 reveal reveal-d4">
              <Link
                to="/profile"
                className="btn-couture"
                data-testid="hero-cta-profile"
              >
                The Profile <ArrowRight size={13} className="arrow" />
              </Link>
              <Link
                to="/gallery"
                className="btn-couture"
                data-testid="hero-cta-gallery"
              >
                The Gallery <ArrowRight size={13} className="arrow" />
              </Link>
            </div>
          </div>

          {/* RIGHT — portrait */}
          <div className="lg:col-span-5 relative reveal reveal-d2 mt-10 lg:mt-0">
            <div className="relative mx-auto w-full max-w-xs sm:max-w-sm lg:max-w-none">
              <div className="img-frame aspect-[3/4] overflow-hidden">
                <img
                  src={profile.hero_portrait}
                  alt="Dr. P. B. Boss"
                  className="w-full h-full object-cover img-soft"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>

        {/* bottom scroll mark */}
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2">
          <span className="font-marcellus uppercase tracking-[0.42em] text-[0.62rem] text-[var(--ink-muted)]">
            Continue
          </span>
          <span className="w-px h-12 bg-[var(--gold)]" />
        </div>
      </section>

      {/* MANTRA TICKER */}
      <MantraTicker />

      {/* =====================================================
          STATS — pure typography, no boxes
          ===================================================== */}
      <section
        data-testid="stats-section"
        className="py-28 lg:py-40 bg-white"
      >
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <div className="text-center mb-20">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)]">
              A measure of the journey
            </p>
          </div>

          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-y-16 lg:gap-y-0"
            variants={staggerContainer(0.14)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                data-testid={`stat-${i}`}
                variants={staggerItem}
                className={`text-center px-6 ${
                  i < stats.length - 1
                    ? "lg:border-r border-[var(--gold-line)]"
                    : ""
                }`}
              >
                <div className="font-couture text-7xl lg:text-[8rem] xl:text-[10rem] text-[var(--ink)] leading-none tracking-[-0.03em]">
                  {s.value}
                </div>
                <div className="mt-7 font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)]">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* =====================================================
          A LETTER FROM THE CHAIRMAN
          ===================================================== */}
      <section
        data-testid="about-snippet"
        className="py-32 lg:py-48 bg-[var(--bg-soft)] relative"
      >
        <span className="hidden lg:block absolute left-7 top-20 v-label">
          A letter — from the chairman
        </span>

        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-28 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <div className="img-frame aspect-[4/5] overflow-hidden">
              <img
                src={profile.lounge_portrait}
                alt="Dr. P. B. Boss"
                className="w-full h-full object-cover img-soft"
                loading="lazy"
              />
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7 order-1 lg:order-2">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-7">
              A letter from the chairman
            </p>
            <h2 className="font-couture text-5xl sm:text-6xl lg:text-7xl xl:text-[5.6rem] leading-[0.98] tracking-[-0.02em] text-[var(--ink)]">
              A life built on
              <br />
              <span className="font-couture-italic text-[var(--gold-deep)]">
                quality, connection
              </span>
              <br />
              & conviction.
            </h2>

            <div className="mt-14 grid grid-cols-12 gap-5">
              <span className="col-span-2 mt-4 h-px bg-[var(--gold)]" />
              <p className="col-span-10 font-couture-italic text-2xl lg:text-[1.7rem] leading-[1.5] text-[var(--ink-soft)]">
                From a single travel agency in 1993 to a multi-vertical
                enterprise spanning seven countries — the House of Osaka has
                grown alongside a single conviction. That technology, married to
                integrity and service, can elevate every life it touches.
              </p>
            </div>

            <div className="mt-16 flex items-center gap-12">
              <Link
                to="/profile"
                className="btn-couture"
                data-testid="about-read-more"
              >
                The full profile <ArrowRight size={13} className="arrow" />
              </Link>
              <span className="font-couture-italic text-2xl text-[var(--gold-deep)] hidden md:inline">
                — PBB
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =====================================================
          BRANDS — quiet, very slow marquee
          ===================================================== */}
      <section
        data-testid="brands-section"
        className="py-24 lg:py-32 bg-white overflow-hidden"
      >
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 mb-14 text-center">
          <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)]">
            The Osaka family of brands
          </p>
        </div>
        <Marquee gradient gradientColor="#ffffff" gradientWidth={200} speed={22} pauseOnHover>
          {brands.map((b, i) => (
            <div
              key={`${b.name}-${i}`}
              className="mx-16 flex items-center justify-center"
              data-testid={`brand-${i}`}
            >
              <img
                src={b.logo}
                alt={b.name}
                className="h-20 lg:h-24 w-auto object-contain opacity-85 hover:opacity-100 transition-opacity"
                loading="lazy"
              />
            </div>
          ))}
        </Marquee>
      </section>

      {/* =====================================================
          HONOURS — chronological list, NO cards
          ===================================================== */}
      <section
        data-testid="featured-awards"
        className="py-32 lg:py-48 bg-[var(--bg-soft)] relative"
      >
        <span className="hidden lg:block absolute right-7 top-20 v-label">
          II — Recognition
        </span>

        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <Reveal className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
            <div className="lg:col-span-8">
              <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-6">
                Vol. II — Honours
              </p>
              <h2 className="font-couture text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[0.98]">
                A constellation
                <br />
                <span className="font-couture-italic text-[var(--gold-deep)]">
                  of honours.
                </span>
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/awards"
                className="btn-couture"
                data-testid="featured-awards-cta"
              >
                The full chronicle
                <ArrowRight size={13} className="arrow" />
              </Link>
            </div>
          </Reveal>

          {/* Chronological list */}
          <motion.ul
            className="border-t border-[var(--gold-line)]"
            variants={staggerContainer(0.1)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-70px" }}
          >
            {featuredAwards.map((a, i) => (
              <motion.li
                key={i}
                data-testid={`featured-award-${i}`}
                variants={staggerItem}
                className="group grid grid-cols-12 gap-6 py-7 lg:py-9 border-b border-[var(--gold-line)] items-center transition-colors hover:bg-white"
              >
                <span className="col-span-12 lg:col-span-1 numeral text-2xl text-[var(--gold-deep)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="col-span-6 lg:col-span-2 font-marcellus uppercase tracking-[0.28em] text-[0.7rem] text-[var(--ink-muted)]">
                  {a.date}
                </span>
                <h3 className="col-span-12 lg:col-span-7 font-couture text-2xl lg:text-3xl text-[var(--ink)] leading-snug">
                  {a.title}
                </h3>
                <div className="hidden lg:block col-span-2">
                  <div className="aspect-[4/3] overflow-hidden img-frame">
                    <img
                      src={a.image}
                      alt={a.title}
                      className="w-full h-full object-cover img-soft"
                      loading="lazy"
                    />
                  </div>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* =====================================================
          PRESS — editorial
          ===================================================== */}
      <section data-testid="featured-news" className="py-32 lg:py-48 bg-white">
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <Reveal className="text-center mb-20">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-6">
              Vol. III — Press
            </p>
            <h2 className="font-couture text-5xl sm:text-6xl lg:text-7xl tracking-[-0.02em] leading-[1]">
              Voices,
              <span className="font-couture-italic text-[var(--gold-deep)]">
                {" "}headlines{" "}
              </span>
              & features.
            </h2>
          </Reveal>

          <motion.div
            className="grid md:grid-cols-2 gap-14 lg:gap-24"
            variants={staggerContainer(0.16)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            {featuredNews.map((n, i) => (
              <motion.article
                key={i}
                data-testid={`featured-news-${i}`}
                variants={staggerItem}
                className="group"
              >
                <div className="img-frame aspect-[16/11] overflow-hidden mb-8">
                  <img
                    src={n.image}
                    alt={n.title}
                    className="w-full h-full object-cover img-soft"
                    loading="lazy"
                  />
                </div>
                <p className="font-marcellus uppercase tracking-[0.34em] text-[0.7rem] text-[var(--gold-deep)] mb-4">
                  {n.date}
                </p>
                <h3 className="font-couture text-3xl lg:text-4xl text-[var(--ink)] leading-tight tracking-[-0.01em]">
                  {n.title}
                </h3>
              </motion.article>
            ))}
          </motion.div>

          <div className="mt-20 text-center">
            <Link to="/news" className="btn-couture" data-testid="featured-news-cta">
              All press coverage
              <ArrowRight size={13} className="arrow" />
            </Link>
          </div>
        </div>
      </section>

      {/* CLOSING INVITATION */}
      <section
        data-testid="home-cta"
        className="relative py-40 lg:py-56 bg-[var(--bg-soft)] overflow-hidden text-center"
      >
        <Reveal className="max-w-3xl mx-auto px-7">
          <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)]">
            An invitation
          </p>
          <h2 className="mt-12 font-couture text-6xl sm:text-7xl lg:text-[6.5rem] tracking-[-0.025em] text-[var(--ink)] leading-[1]">
            Coffee.
            <br />
            <span className="font-couture-italic text-[var(--gold-deep)]">
              Conversation.
            </span>
            <br />
            Collaboration.
          </h2>
          <p className="mt-12 font-couture-italic text-xl lg:text-2xl text-[var(--ink-soft)] max-w-xl mx-auto">
            For partnerships, speaking engagements or a meaningful exchange of
            ideas — write directly.
          </p>
          <div className="mt-16 flex justify-center">
            <Link to="/contact" className="btn-couture-solid" data-testid="cta-contact">
              Reach the chairman
              <ArrowRight size={13} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
